import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { noteRateLimited, sleep } from "./apiThrottle";

export const ACCESS_TOKEN_KEY = "access_token";
export const WS_ACCESS_TOKEN_KEY = "ws_access_token";
export const REFRESH_TOKEN_KEY = "refresh_token";
// Nuxt-совместимые ключи для localStorage (с префиксом auth.)
export const AUTH_TOKEN_PASSWORD_GRANT = "auth._token.password_grant";
export const AUTH_TOKEN_WS_PASSWORD_GRANT = "auth._token.ws_password_grant";
export const AUTH_REFRESH_TOKEN_PASSWORD_GRANT = "auth._refresh_token.password_grant";
export const AUTH_STRATEGY = "auth.strategy";

// Ключи для proctoring.js (БЕЗ префикса auth.)
export const TOKEN_PASSWORD_GRANT = "_token.password_grant";
export const TOKEN_WS_PASSWORD_GRANT = "_token.ws_password_grant";

/**
 * Приватный axios-клиент — работает с токеном и авто-refresh логикой
 */
const axiosPrivateClient: AxiosInstance = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_API_BASE_URL}/api`,
  timeout: 300000,
  headers: { "Content-Type": "application/json" },
});

axiosPrivateClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token =
      typeof window !== "undefined"
        ? localStorage.getItem(ACCESS_TOKEN_KEY)
        : null;
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // Если отправляем FormData, удаляем Content-Type - браузер сам установит правильный с boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

const AUTH_KEYS = [
  ACCESS_TOKEN_KEY,
  WS_ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  AUTH_TOKEN_PASSWORD_GRANT,
  AUTH_TOKEN_WS_PASSWORD_GRANT,
  AUTH_REFRESH_TOKEN_PASSWORD_GRANT,
  AUTH_STRATEGY,
  TOKEN_PASSWORD_GRANT,
  TOKEN_WS_PASSWORD_GRANT,
];

const REFRESH_LOCK = "te-auth-refresh";

const NOT_AUTHENTICATED = "0102";

// Паузы между повторами запроса с непринятым токеном — до обновления токена. Последний повтор
// через 7,5 с: к этому времени свежий токен бэкенд уже принимает стабильно.
const AUTH_RETRY_DELAYS_MS = [500, 1000, 2000, 4000];

type RefreshOutcome =
  | { ok: true; token: string }
  | { ok: false; grantRejected: boolean };

let refreshing: Promise<RefreshOutcome> | null = null;

const readStored = (key: string): string | null => {
  if (typeof window === "undefined") return null;

  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

/**
 * Единственная точка записи сессии: раскладывает токены по всем ключам, которые читают
 * фронт и proctoring.js — наши, Nuxt-совместимые `auth._token.*` и беспрефиксные `_token.*`.
 * Отсутствующие в ответе поля сохранённые не затирают: refresh возвращает не весь набор.
 */
export const storeTokens = (data: {
  access_token: string;
  refresh_token?: string;
  ws_access_token?: string;
}): void => {
  const bearer = `Bearer ${data.access_token}`;

  localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
  localStorage.setItem(AUTH_TOKEN_PASSWORD_GRANT, bearer);
  localStorage.setItem(TOKEN_PASSWORD_GRANT, bearer);

  if (data.refresh_token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
    localStorage.setItem(AUTH_REFRESH_TOKEN_PASSWORD_GRANT, data.refresh_token);
  }

  if (data.ws_access_token) {
    localStorage.setItem(WS_ACCESS_TOKEN_KEY, data.ws_access_token);
    localStorage.setItem(AUTH_TOKEN_WS_PASSWORD_GRANT, data.ws_access_token);
    localStorage.setItem(TOKEN_WS_PASSWORD_GRANT, data.ws_access_token);
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("auth-token-changed"));
  }
};

const forgetSession = (): void => {
  for (const key of AUTH_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      continue;
    }
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("auth-token-changed"));
    window.location.href = "/auth";
  }
};

const grantRejected = (error: unknown): boolean => {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;

  if (typeof status !== "number") return false;

  return status === 400 || status === 401;
};

/**
 * Бэкенд не принял токен. На мёртвый он отвечает 400 со status_code 0102, а не 401, — и тем же
 * ответом на исправный, но только что выданный: первые секунды такой принимается через раз.
 */
const tokenRejected = (error: unknown): boolean => {
  const response = (
    error as { response?: { status?: unknown; data?: { status_code?: unknown } } } | null
  )?.response;

  return (
    response?.status === 401 ||
    (response?.status === 400 && response.data?.status_code === NOT_AUTHENTICATED)
  );
};

const runRefresh = async (tokenBefore: string | null): Promise<RefreshOutcome> => {
  const current = readStored(ACCESS_TOKEN_KEY);

  if (current && current !== tokenBefore) return { ok: true, token: current };

  const refresh = readStored(REFRESH_TOKEN_KEY);

  if (!refresh) return { ok: false, grantRejected: true };

  try {
    const { data } = await axios.post(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/oauth/token`,
      {
        grant_type: "refresh_token",
        client_id: process.env.NEXT_PUBLIC_PASSPORT_PASSWORD_GRANT_ID,
        client_secret: process.env.NEXT_PUBLIC_PASSPORT_PASSWORD_GRANT_SECRET,
        refresh_token: refresh,
      }
    );

    storeTokens(data);

    return { ok: true, token: data.access_token };
  } catch (error) {
    return { ok: false, grantRejected: grantRejected(error) };
  }
};

const withRefreshLock = async (
  task: () => Promise<RefreshOutcome>
): Promise<RefreshOutcome> => {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;

  if (!locks) return task();

  return await locks.request(REFRESH_LOCK, task);
};

const refreshSession = (tokenBefore: string | null): Promise<RefreshOutcome> => {
  if (refreshing) return refreshing;

  const started = withRefreshLock(() => runRefresh(tokenBefore));

  refreshing = started.finally(() => {
    refreshing = null;
  });

  return refreshing;
};

axiosPrivateClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 429) {
      noteRateLimited(error.response?.headers?.["retry-after"]);
    }

    if (!tokenRejected(error) || !original) {
      return Promise.reject(error);
    }

    const retries = original._authRetries ?? 0;

    if (retries < AUTH_RETRY_DELAYS_MS.length && readStored(ACCESS_TOKEN_KEY)) {
      original._authRetries = retries + 1;
      await sleep(AUTH_RETRY_DELAYS_MS[retries]);
      return axiosPrivateClient(original);
    }

    if (original._retry) return Promise.reject(error);

    original._retry = true;
    // свежий токен после обновления бэкенд тоже принимает не сразу
    original._authRetries = 0;

    const sentWith = String(original.headers?.Authorization ?? "");
    const stored = readStored(ACCESS_TOKEN_KEY);

    if (stored && sentWith !== `Bearer ${stored}`) {
      original.headers.Authorization = `Bearer ${stored}`;
      return axiosPrivateClient(original);
    }

    if (!readStored(REFRESH_TOKEN_KEY)) return Promise.reject(error);

    const outcome = await refreshSession(stored);

    if (outcome.ok) {
      original.headers.Authorization = `Bearer ${outcome.token}`;
      return axiosPrivateClient(original);
    }

    const afterRefresh = readStored(ACCESS_TOKEN_KEY);

    if (afterRefresh && afterRefresh !== stored) {
      original.headers.Authorization = `Bearer ${afterRefresh}`;
      return axiosPrivateClient(original);
    }

    if (outcome.grantRejected) forgetSession();

    return Promise.reject(error);
  }
);

export default axiosPrivateClient;
